/**
 * 统一日志模块。
 *
 * 目标：为 basksoft-ai 报表生成器提供一套轻量、零依赖的日志能力，CLI 与 core 层
 * 共用同一实现。日志同时写入日志文件（cwd 下 `.baskreport/baskreport.log`，append）与 stderr，
 * 但**绝不**写入 stdout，以保证 `--out` 落盘的 JSON/XML 结果以及管道输出不被污染。
 *
 * 设计要点（与 budget.ts 一致）：
 * - 零运行时依赖：自实现约百行，避免引入供应链/体积风险。
 * - 同步追加写 + fail-open：文件写异常时仅降级为「不写文件、仍走 stderr」，绝不抛错中断命令。
 * - 级别解析链：`BASK_LOG_LEVEL` 环境变量 > 默认 `info`；未知级别回退 `info`。
 * - 开关：`BASK_LOG_DISABLE=true` 时所有 logger 方法变 no-op（回到无日志态）。
 * - 路径：`BASK_LOG_FILE` 指定，否则 `<状态目录>/.baskreport/baskreport.log`（自动创建
 *    `.baskreport/` 目录，与 `.baskreport/budget.json` 统一收敛到运行时目录，已被 .gitignore 忽略）。
 *    状态目录 = 显式 --config-dir / BASK_CONFIG_DIR，缺省 process.cwd()（由 config.ts 注入解析器）。
 * - 不记录密钥/token 明文；budget 统计值（limit/used）属非敏感，可记。
 */

import { appendFileSync, mkdirSync } from 'node:fs';
import { dirname, resolve } from 'node:path';

/** 支持的最低→最高日志级别。 */
export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

/** 各级别对应的数值权重（越大越严重）。 */
const LEVEL_WEIGHT: Record<LogLevel, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
};

/** 默认级别。 */
const DEFAULT_LEVEL: LogLevel = 'info';

/** 运行时状态目录名（cwd 下，dotfile，项目级）。会被 .gitignore 忽略。 */
export const LOG_DIR_NAME = '.baskreport';

/** 日志文件名（位于 .baskreport/ 目录内）。 */
export const LOG_FILE_NAME = 'baskreport.log';

/** Logger 接口，业务代码仅依赖此抽象。 */
export interface Logger {
  debug(msg: string, meta?: unknown): void;
  info(msg: string, meta?: unknown): void;
  warn(msg: string, meta?: unknown): void;
  error(msg: string, err?: unknown): void;
}

/** 已解析的全局日志配置（level/disabled 带缓存；文件路径实时计算）。 */
interface ResolvedConfig {
  level: LogLevel;
  disabled: boolean;
}

let cached: ResolvedConfig | null = null;

/** 运行时状态目录解析器：缺省 process.cwd()；config.ts 注入后跟随 --config-dir。 */
let stateDirResolver: () => string = () => process.cwd();

/**
 * 注入运行时状态目录解析器（预算 budget.json 与日志统一落盘的目录）。
 * 由 config.ts 在模块加载时调用，使日志默认路径跟随 --config-dir / BASK_CONFIG_DIR；
 * 无显式配置目录时解析器回退 process.cwd()，保持既有行为。
 */
export function setStateDirResolver(resolver: () => string): void {
  stateDirResolver = resolver;
}

/** 测试专用：恢复默认解析器（回退 process.cwd()）。 */
export function __resetStateDirResolverForTest(): void {
  stateDirResolver = () => process.cwd();
}

/** 默认日志文件绝对路径（实时计算，不缓存——--config-dir 可能在任意时机被 CLI 注入）。 */
function defaultLogFile(): string {
  return resolve(stateDirResolver(), LOG_DIR_NAME, LOG_FILE_NAME);
}

/** 解析日志配置（level/disabled 带缓存；文件路径在写入时实时解析）。 */
function resolveConfig(): ResolvedConfig {
  if (cached) return cached;
  const disabled = /^(1|true|yes|on)$/i.test(process.env.BASK_LOG_DISABLE ?? '');
  const rawLevel = (process.env.BASK_LOG_LEVEL ?? '').toLowerCase().trim();
  const level: LogLevel =
    rawLevel in LEVEL_WEIGHT ? (rawLevel as LogLevel) : DEFAULT_LEVEL;
  cached = { level, disabled };
  return cached;
}

/** 重置配置缓存（仅测试用；业务代码无需调用）。 */
export function __resetLogConfigForTest(): void {
  cached = null;
}

/** 将任意 meta/err 序列化为可读字符串（避免泄露过多结构，且不抛错）。 */
function serializeExtra(extra: unknown): string {
  if (extra === undefined) return '';
  if (extra instanceof Error) {
    return extra.stack ?? `${extra.name}: ${extra.message}`;
  }
  if (typeof extra === 'string') return extra;
  try {
    const s = JSON.stringify(extra);
    // JSON 化可能丢失（如 BigInt、循环），退回 String
    return s && s !== '{}' ? s : String(extra);
  } catch {
    return String(extra);
  }
}

/** 格式化单行日志。 */
function format(level: LogLevel, module: string, msg: string, extra?: unknown): string {
  const ts = new Date().toISOString();
  const head = `${ts} [${level.toUpperCase()}] [${module}] ${msg}`;
  const tail = extra !== undefined ? ` ${serializeExtra(extra)}` : '';
  return `${head}${tail}\n`;
}

/** 写入日志文件（自动创建目录；fail-open：异常静默忽略）。 */
function writeToFile(file: string, line: string): void {
  try {
    mkdirSync(dirname(file), { recursive: true });
    appendFileSync(file, line, 'utf-8');
  } catch {
    // fail-open：文件不可写时仅丢失文件日志，不影响 stderr 与业务
  }
}

/**
 * 按模块名获取 logger。
 * @param module 调用方模块标识，如 'cli:generate'、'core:ir'、'core:budget'。
 */
export function getLogger(module: string): Logger {
  const doLog = (level: LogLevel, msg: string, extra?: unknown): void => {
    const cfg = resolveConfig();
    if (cfg.disabled) return;
    if (LEVEL_WEIGHT[level] < LEVEL_WEIGHT[cfg.level]) return; // 低于阈值，no-op
    const line = format(level, module, msg, extra);
    // stderr：不污染 stdout
    try {
      process.stderr.write(line);
    } catch {
      /* 忽略 */
    }
    // appendFileSync 会自动创建文件，无需预检；文件路径实时解析（跟随状态目录）
    const file = process.env.BASK_LOG_FILE
      ? resolve(process.env.BASK_LOG_FILE)
      : defaultLogFile();
    if (file) {
      writeToFile(file, line);
    }
  };

  return {
    debug(msg: string, meta?: unknown): void {
      doLog('debug', msg, meta);
    },
    info(msg: string, meta?: unknown): void {
      doLog('info', msg, meta);
    },
    warn(msg: string, meta?: unknown): void {
      doLog('warn', msg, meta);
    },
    error(msg: string, err?: unknown): void {
      doLog('error', msg, err);
    },
  };
}
